import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hallo! Ich bin der digitale Assistent von DROPS TFR. Wie kann ich dir helfen — Drohnen-Inspektion, RTK-Vermessung oder ein Angebot?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, showForm]);

  const openForm = () => {
    const summary = messages.filter((m) => m.role === "user").map((m) => m.text).join(" | ");
    setForm((f) => ({ ...f, message: f.message || (summary ? `Aus dem Chat: ${summary}` : "") }));
    setShowForm(true);
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { name: form.name, email: form.email, message: form.message });
      setShowForm(false);
      setForm({ name: "", email: "", message: "" });
      setMessages((m) => [...m, { role: "bot", text: "Perfekt — deine Anfrage ist bei DROPS TFR eingegangen. Wir melden uns schnellstmöglich per E-Mail bei dir. 🚁" }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Das Senden hat nicht geklappt. Bitte versuche es später noch einmal oder nutze das Kontaktformular." }]);
    } finally { setSending(false); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "bot", text: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "bot", text: "Entschuldige, ich bin gerade nicht erreichbar. Bitte nutze das Kontaktformular." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Chat öffnen"
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center bg-[#F5B301] text-black shadow-lg transition-transform duration-300 hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-6 z-[60] flex h-[30rem] w-[calc(100vw-3rem)] max-w-sm flex-col border border-white/15 bg-[#0D0D0F] shadow-2xl">
          <div className="flex items-center gap-3 border-b border-white/10 bg-black/40 px-4 py-3">
            <span className="flex h-2 w-2 animate-pulse rounded-full bg-[#F5B301]" />
            <div>
              <p className="font-tac text-xs font-bold tracking-[0.2em] text-white">DROPS TFR ASSISTENT</p>
              <p className="font-tac text-[9px] tracking-[0.15em] text-white/40">Fragen zu Inspektion & Vermessung</p>
            </div>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}`} className={`max-w-[85%] border p-2.5 ${m.role === "user" ? "ml-auto border-[#F5B301]/30 bg-[#F5B301]/5" : "border-white/10 bg-white/[0.02]"}`}>
                <p className="font-tac text-xs leading-relaxed text-white/85">{m.text}</p>
              </div>
            ))}
            {loading && <div className="flex items-center gap-2 font-tac text-[10px] text-white/40"><Loader2 className="h-3 w-3 animate-spin" /> schreibt…</div>}
            {showForm && (
              <form data-testid="chat-enquiry-form" onSubmit={submitEnquiry} className="space-y-2 border border-[#F5B301]/30 bg-[#F5B301]/5 p-3">
                <p className="font-tac text-[10px] tracking-[0.2em] text-[#F5B301]">ANFRAGE AN DROPS TFR</p>
                <input data-testid="chat-enquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full border-b border-white/20 bg-transparent py-1.5 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none" />
                <input data-testid="chat-enquiry-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail" className="w-full border-b border-white/20 bg-transparent py-1.5 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none" />
                <textarea data-testid="chat-enquiry-message" required rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Dein Anliegen" className="w-full resize-none border-b border-white/20 bg-transparent py-1.5 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none" />
                <div className="flex items-center gap-2">
                  <button data-testid="chat-enquiry-submit" type="submit" disabled={sending} className="flex items-center gap-1.5 bg-[#F5B301] px-4 py-2 font-tac text-[10px] font-bold tracking-[0.2em] text-black disabled:opacity-60">{sending && <Loader2 className="h-3 w-3 animate-spin" />} SENDEN</button>
                  <button type="button" onClick={() => setShowForm(false)} className="font-tac text-[10px] tracking-[0.2em] text-white/50 hover:text-white">ABBRECHEN</button>
                </div>
              </form>
            )}
            <div ref={endRef} />
          </div>
          {!showForm && (
            <button data-testid="chat-enquiry-open" onClick={openForm} className="border-t border-white/10 bg-white/[0.02] py-2.5 font-tac text-[10px] tracking-[0.2em] text-[#F5B301] transition-colors hover:bg-[#F5B301]/10">
              ＋ ANFRAGE AN DROPS TFR SENDEN
            </button>
          )}
          <div className="flex items-center gap-2 border-t border-white/10 p-3">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Nachricht schreiben…"
              className="flex-1 border-b border-white/20 bg-transparent py-2 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none"
            />
            <button data-testid="chat-send" onClick={send} disabled={loading} className="flex h-9 w-9 items-center justify-center bg-[#F5B301] text-black disabled:opacity-60"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
